import React, { useMemo, useState, useEffect } from "react";

import { Pencil, Trash2 } from "lucide-react";

import { SponsorData } from "..";
import Button from "../../../Components/Button";
import CommonInput from "../../../Components/Input";
import Select from "../../../Components/Select";
import StepProgressBar from "../../../Components/StepProgressBar";
import Table from "../../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchAdminUsers,
  fetchRoles,
} from "../../../Store/Reducers/MasterSlice";
import {
  createUsersBulk,
  BulkCreateUsersPayload,
  UserPayload,
  ExecutiveProfilePayload,
  clearCreateUsersBulkError,
  clearCreateUsersBulkSuccess,
  fetchExecutiveSponsors,
} from "../../../Store/Reducers/OpsSlice";
import { fetchVendorAdminGrades } from "../../../Store/Reducers/VendorAdminSlice";
import { regionOptions } from "../../../Utils/Constants/Constants";
import { EMAIL_REGEX } from "../../../Utils/Constants/RegexConstants";
import {
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
  ROLES,
} from "../../../Utils/Constants/RoleConstants";

type ExecutiveForm = {
  name: string;
  title: string;
  email: string;
  phone: string;
  region: string;
  grade: string;
  city: string | null;
  country: string | null;
  specialization: string;
  certifications: string;
  maxAccounts: string;
  languages: string;
  role: string; // fixed Sponsor
};

const emptyExecutive: ExecutiveForm = {
  name: "",
  title: "",
  email: "",
  phone: "",
  region: "",
  grade: "",
  city: "",
  country: "",
  specialization: "",
  certifications: "",
  maxAccounts: "",
  languages: "",
  role: "",
};

type Props = {
  onClose: () => void;
  isEditMode?: boolean;
  executiveToEdit?: SponsorData | null;
};

const AddExecutive: React.FC<Props> = ({
  onClose,
  isEditMode = false,
  executiveToEdit = null,
}) => {
  const dispatch = useAppDispatch();
  const { loading, error, success } = useAppSelector(
    state => state.ops.createUsersBulk
  );
  const { items: adminUsers } = useAppSelector(
    state => state.master.adminUsers
  );
  const { items: roles } = useAppSelector(state => state.master.roles);
  const { grades } = useAppSelector(state => state.vendorAdmin);
  const generateDefaultBatchName = () =>
    isEditMode
      ? `Executives Update - ${new Date().toLocaleString()}`
      : `Executives Add - ${new Date().toLocaleString()}`;

  const [form, setForm] = useState<ExecutiveForm>(emptyExecutive);
  const [execs, setExecs] = useState<ExecutiveForm[]>([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [batchName, setBatchName] = useState(() => generateDefaultBatchName());
  const [defaultBatchName, setDefaultBatchName] = useState(() =>
    generateDefaultBatchName()
  );

  // Transform admin users to approver options
  const approverOptions = useMemo(() => {
    return adminUsers.map(user => {
      const roleName = user.role || "";
      const normalizedRole = normalizeRoleName(roleName);
      const roleDisplay =
        ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES] ||
        roleName ||
        "";

      return {
        label: `${user.full_name} - ${roleDisplay}`,
        value: user.user_id,
      };
    });
  }, [adminUsers]);

  // Transform roles to options and find sponsor role
  const roleOptions = useMemo(() => {
    return roles.map(role => {
      const normalizedRole = normalizeRoleName(role.name);
      const roleDisplay =
        ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES] ||
        role.name ||
        "";
      return {
        label: roleDisplay,
        value: role.role_id,
      };
    });
  }, [roles]);

  const sponsorRoleId = useMemo(() => {
    const sponsorRole = roles.find(r => r.name === ROLES.EXECUTIVE_SPONSOR);
    return sponsorRole?.role_id || "";
  }, [roles]);

  const [approver, setApprover] = useState<string>("");

  // Generate grade options from fetched grades
  const gradeOptions = useMemo(() => {
    if (grades?.items && Array.isArray(grades.items)) {
      return grades.items.map((item: any) => ({
        label: item.grade_name,
        value: String(item.grade),
      }));
    }
    return [];
  }, [grades]);

  // Create mapping from grade name to max accounts (capacity_limits)
  const gradeToMaxAccountsMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (grades?.items && Array.isArray(grades.items)) {
      grades.items.forEach((item: any) => {
        map[String(item.grade)] = item.capacity_limits || 0;
      });
    }
    return map;
  }, [grades]);

  // Fetch admin users on component mount
  useEffect(() => {
    if (adminUsers.length === 0) {
      dispatch(fetchAdminUsers());
    }
  }, [dispatch, adminUsers.length]);

  // Fetch grades on component mount
  useEffect(() => {
    if (!grades) {
      dispatch(fetchVendorAdminGrades());
    }
  }, [dispatch, grades]);

  // Pre-fill form when in edit mode
  useEffect(() => {
    if (isEditMode && executiveToEdit) {
      const formData: ExecutiveForm = {
        name: executiveToEdit.name,
        title: executiveToEdit.title,
        email: executiveToEdit.email,
        phone: executiveToEdit.phone || "",
        region: executiveToEdit.region,
        grade: executiveToEdit.grade ? String(executiveToEdit.grade) : "",
        city: executiveToEdit.city,
        country: executiveToEdit.country,
        specialization:
          executiveToEdit.specialization ||
          executiveToEdit.sphereOfExcellence ||
          "",
        certifications: executiveToEdit.certifications?.join(", ") || "",
        maxAccounts: executiveToEdit.accounts.toString(),
        languages: executiveToEdit?.languages?.join(",") || "",
        role: sponsorRoleId || "",
      };

      setForm(formData);
      setStep(1);
      setExecs([formData]);
      setEditingIndex(0);
      const defaultName = `Executives Update - ${new Date().toLocaleString()}`;
      setBatchName(defaultName);
      setDefaultBatchName(defaultName);
    }
  }, [isEditMode, executiveToEdit, sponsorRoleId]);
  // Fetch roles on component mount
  useEffect(() => {
    if (roles.length === 0) {
      dispatch(fetchRoles());
    }
  }, [dispatch, roles.length]);

  // Set default role to sponsor when roles are available
  useEffect(() => {
    if (sponsorRoleId && !form.role) {
      setForm(prev => ({ ...prev, role: sponsorRoleId }));
    }
  }, [sponsorRoleId, form.role]);

  // Set default approver when options are available
  useEffect(() => {
    if (approverOptions.length > 0 && !approver) {
      setApprover(approverOptions[0].value);
    }
  }, [approverOptions, approver]);

  // Handle success
  useEffect(() => {
    if (success) {
      dispatch(clearCreateUsersBulkSuccess());
      // Refresh the executive sponsors list
      dispatch(fetchExecutiveSponsors({}));
      onClose();
    }
  }, [success, dispatch, onClose]);

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error("Error creating users:", error);
      dispatch(clearCreateUsersBulkError());
    }
  }, [error, dispatch]);

  const emailError = useMemo(() => {
    const email = form.email.trim();
    if (!email) return "";
    return EMAIL_REGEX.test(email) ? "" : "Please enter a valid email address.";
  }, [form.email]);

  const canAdd = useMemo(() => {
    return (
      form.name.trim() &&
      form.title.trim() &&
      form.email.trim() &&
      !emailError &&
      form.region.trim() &&
      form.grade.trim() &&
      (form.city ?? "").trim() &&
      (form.country ?? "").trim() &&
      form.maxAccounts.trim()
    );
  }, [form, emailError]);

  const handleChange =
    (key: keyof ExecutiveForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isEditMode && key === "email") return;
      setForm(prev => ({ ...prev, [key]: e.target.value }));
    };

  const handleSelect = (key: keyof ExecutiveForm) => (val: string) => {
    if (key === "grade") {
      // When grade changes, automatically set maxAccounts based on the grade
      const maxAccounts = gradeToMaxAccountsMap[val] || "0";
      setForm(prev => ({
        ...prev,
        grade: val,
        maxAccounts: maxAccounts.toString(),
      }));
    } else {
      setForm(prev => ({ ...prev, [key]: val }));
    }
  };

  const handleAddToList = () => {
    if (isEditMode || !canAdd) return;
    if (editingIndex !== null) {
      setExecs(prev => prev.map((e, i) => (i === editingIndex ? form : e)));
      setEditingIndex(null);
    } else {
      setExecs(prev => [...prev, form]);
    }
    setForm({ ...emptyExecutive, role: sponsorRoleId });
  };

  const handleEdit = (i: number) => {
    setForm(execs[i]);
    setEditingIndex(i);
  };

  const handleDelete = (i: number) => {
    if (isEditMode) return;
    setExecs(prev => prev.filter((_, idx) => idx !== i));
  };

  // In edit mode, keep the single executive synced with form changes.
  useEffect(() => {
    if (!isEditMode) return;
    setExecs([form]);
  }, [form, isEditMode]);

  const handleReview = () => {
    if (isEditMode) {
      if (!canAdd) return;
      setExecs([form]);
    }
    setStep(2);
  };

  // Map form data to API payload
  const mapFormToUserPayload = (formData: ExecutiveForm): UserPayload => {
    // Parse languages string to array if needed
    const languagesArray = formData.languages
      ? formData?.languages
          ?.split(",")
          .map(lang => lang.trim())
          .filter(Boolean)
      : [];

    const certificationsArray = formData.certifications
      ? formData.certifications
          .split(",")
          .map(cert => cert.trim())
          .filter(Boolean)
      : [];

    // Parse specialties from specialization
    const specialties = formData.specialization
      ? { domains: [formData.specialization] }
      : null;

    // Form now stores numeric grade as string from select option value.
    let gradeNumber: number | null = null;
    if (formData.grade) {
      const parsedGrade = Number(formData.grade);
      if (!Number.isNaN(parsedGrade)) {
        gradeNumber = parsedGrade;
      } else if (grades?.items) {
        // Backward compatibility if an old grade_name value is present.
        const gradeItem = grades.items.find(
          (item: any) => item.grade_name === formData.grade
        );
        gradeNumber = gradeItem ? gradeItem.grade : null;
      }
    }

    const executiveProfile: ExecutiveProfilePayload = {
      city: formData.city || null,
      country: formData.country || null,
      region: formData.region || null,
      specialization: formData.specialization || null,
      experience_level: null,
      account_limit: formData.maxAccounts
        ? parseInt(formData.maxAccounts, 10)
        : null,
      max_accounts: formData.maxAccounts
        ? parseInt(formData.maxAccounts, 10)
        : null,
      current_accounts: 0,
      serving: 0,
      available_capacity: formData.maxAccounts
        ? parseInt(formData.maxAccounts, 10)
        : null,
      specialties: specialties,
      languages: languagesArray.length > 0 ? languagesArray : null,
      certifications:
        certificationsArray.length > 0 ? certificationsArray : null,
      join_date: null,
      grade: gradeNumber ?? null,
      availability_status: "available",
      is_active: true,
    };

    return {
      email: formData.email,
      full_name: formData.name,
      phone: formData.phone || null,
      position: formData.title || null,
      status: null,
      preferences: null,
      last_login_at: null,
      last_login_ip: null,
      role_id: formData.role || null,
      executiveProfile,
    };
  };

  const handleSubmit = () => {
    if (!approver || execs.length === 0) {
      return;
    }

    const resolvedBatchName = batchName.trim();
    const batchNameForBackend =
      !resolvedBatchName || resolvedBatchName === defaultBatchName.trim()
        ? null
        : resolvedBatchName;

    const payload = {
      batch_name: batchNameForBackend,
      approver_id: approver,
      users: execs.map(mapFormToUserPayload),
      upload_type: isEditMode ? "user_update" : "add_user",
    };

    dispatch(createUsersBulk(payload as BulkCreateUsersPayload));
  };

  return (
    <div>
      <div className="h-[calc(100vh-10.5rem)] overflow-x-visible overflow-y-auto px-4">
        <div
          className="mb-4 overflow-x-visible min-w-0"
          style={{ ["--step-track-bg" as string]: "#fff" }}
        >
          <StepProgressBar
            steps={
              isEditMode
                ? [
                    { label: "Edit Executive" },
                    { label: "Review & Select Approver" },
                  ]
                : [
                    { label: "Add Executives" },
                    { label: "Review & Select Approver" },
                  ]
            }
            currentStepIndex={step}
          />
        </div>

        <div className="pt-4">
          {step === 1 && (
            <div className="add-step">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <CommonInput
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange("name")}
                    required
                    infoText="Max 100 characters, letters and spaces only"
                    placeholder="John Smith"
                  />
                </div>
                <div className="mb-4">
                  <CommonInput
                    label="Title"
                    name="title"
                    value={form.title}
                    onChange={handleChange("title")}
                    required
                    infoText="Max 100 characters"
                    placeholder="Senior Account Executive"
                  />
                </div>

                <div className="mb-4">
                  <CommonInput
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    required
                    disabled={isEditMode}
                    error={emailError}
                    infoText={
                      isEditMode
                        ? "Email cannot be changed for an existing executive sponsor"
                        : "Must be a valid email address"
                    }
                    placeholder="john.smith@company.com"
                  />
                </div>
                <div className="mb-4">
                  <CommonInput
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="+1-555-0123"
                    infoText="Include country code if applicable (e.g. +1-555-0123)"
                  />
                </div>

                <div className="mb-4">
                  <Select
                    label="Region"
                    name="region"
                    value={form.region}
                    options={regionOptions}
                    onChange={e =>
                      handleSelect("region")(String(e.target.value))
                    }
                    required
                  />
                </div>
                <div className="mb-4">
                  <Select
                    label="Grade"
                    name="grade"
                    value={form.grade}
                    options={gradeOptions}
                    onChange={e =>
                      handleSelect("grade")(String(e.target.value))
                    }
                    required
                  />
                </div>

                <div className="mb-4">
                  <CommonInput
                    label="City"
                    name="city"
                    value={form.city || ""}
                    onChange={handleChange("city")}
                    placeholder="San Francisco"
                    required
                  />
                </div>
                <div className="mb-4">
                  <CommonInput
                    label="Country"
                    name="country"
                    value={form.country || ""}
                    onChange={handleChange("country")}
                    placeholder="United States"
                    required
                  />
                </div>

                <div className="mb-4">
                  <CommonInput
                    label="Specialization"
                    name="specialization"
                    value={form.specialization}
                    onChange={handleChange("specialization")}
                    placeholder="Enterprise Software"
                    infoText="Max 100 characters"
                  />
                </div>
                <div className="mb-4">
                  <CommonInput
                    label="Max Accounts"
                    name="maxAccounts"
                    value={form.maxAccounts}
                    onChange={() => null}
                    required
                    readOnly
                    placeholder="Max accounts auto-set"
                    infoText="Automatically set based on selected grade"
                  />
                </div>

                <div className="mb-4">
                  <CommonInput
                    label="Certifications"
                    name="certifications"
                    value={form.certifications}
                    onChange={handleChange("certifications")}
                    placeholder="AWS Solutions Architect, Salesforce Admin"
                  />
                </div>
                <div className="mb-4">
                  <CommonInput
                    label="Languages"
                    name="languages"
                    value={form.languages.toString()}
                    onChange={handleChange("languages")}
                    placeholder="English"
                  />
                </div>
                <div className="mb-4">
                  <Select
                    label="Role"
                    name="role"
                    value={form.role}
                    options={roleOptions}
                    onChange={e => handleSelect("role")(String(e.target.value))}
                    disabled={form.role === sponsorRoleId}
                    isShowPlaceHolder={false}
                  />
                </div>
              </div>

              {!isEditMode && execs.length > 0 && (
                <div className="mt-2">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    Executives to be Added ({execs.length})
                  </h3>
                  {execs.map((e, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-4 p-3.5 px-4 border border-gray-200 rounded-lg mb-3 last:mb-0 bg-white"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.9375rem] leading-snug text-gray-800 font-semibold">
                          {e.name}
                        </div>
                        <div className="text-[0.8125rem] leading-snug mt-1 text-gray-500">
                          {e.title} • {e.region} • {e.city}, {e.country}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="clear"
                          onClick={() => handleEdit(idx)}
                          title="Edit"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        >
                          <Pencil size={17} />
                        </Button>
                        <Button
                          variant="clear"
                          onClick={() => handleDelete(idx)}
                          title="Delete"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-4">
                {isEditMode
                  ? "Review Executive"
                  : `Review Executives (${execs.length})`}
              </h3>
              <Table
                className="border rounded-lg mb-6 overflow-hidden"
                columns={[
                  { key: "name", title: "Name" },
                  { key: "title", title: "Title" },
                  { key: "email", title: "Email" },
                  { key: "region", title: "Region" },
                  { key: "grade", title: "Grade" },
                  { key: "maxAccounts", title: "Max Accounts" },
                ]}
                data={execs as any}
              />

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                <h4 className="text-[0.9375rem] font-bold mb-4">
                  Approval Required
                </h4>
                <CommonInput
                  className="mb-6"
                  name="batchName"
                  value={batchName}
                  onChange={e => setBatchName(e.target.value)}
                  label="Batch Name (Optional)"
                />

                <Select
                  name="approver"
                  value={approver}
                  options={approverOptions}
                  onChange={e => setApprover(String(e.target.value))}
                  isShowPlaceHolder={false}
                  required
                  label="Select Approver (Admin, C-Level, or Executive Leader)"
                  className="mb-4"
                />
                <p className="text-[0.8125rem] text-gray-500 mt-3 leading-snug">
                  These {execs.length} executive{execs.length !== 1 ? "s" : ""}{" "}
                  will be sent for approval before activation
                </p>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
      </div>
      <div className="flex justify-between items-center gap-4 flex-wrap min-h-[2.75rem] mt-4 pt-3 border-t px-3">
        {step === 1 && (
          <>
            <Button
              variant="outline-primary"
              className="btn-lg min-h-[2.5rem]"
              onClick={onClose}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              {!isEditMode && (
                <Button
                  variant="primary"
                  className="btn-lg"
                  onClick={handleAddToList}
                  disabled={!canAdd}
                >
                  {editingIndex !== null
                    ? "Update Executive in List"
                    : "+ Add to List"}
                </Button>
              )}
              <Button
                variant="primary"
                className="btn-lg min-h-[2.5rem]"
                onClick={handleReview}
                disabled={isEditMode ? !canAdd : execs.length === 0}
              >
                {isEditMode ? "Review Changes" : "Review Executives"}
              </Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <Button
              variant="outline-primary"
              className="btn-lg"
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className="btn-lg min-h-[2.5rem]"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit for Approval"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AddExecutive;
